import React from 'react'
import { ScrollView, StyleSheet, View } from 'react-native'
import { CommonActions, useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Header, Screen, Text } from '@components'
import { useLanguage } from '@context'
import { useCurrentUserStore } from '@store'

// « Événements » section: one [title, content] pair of translation keys per block.
const EVENTS_BLOCKS = [
  ['privacyPolicy.eventsData', 'privacyPolicy.eventsDataContent'],
  ['privacyPolicy.eventsPurposes', 'privacyPolicy.eventsPurposesContent'],
  ['privacyPolicy.eventsLegalBasis', 'privacyPolicy.eventsLegalBasisContent'],
  ['privacyPolicy.eventsRecipients', 'privacyPolicy.eventsRecipientsContent'],
  ['privacyPolicy.eventsRetention', 'privacyPolicy.eventsRetentionContent'],
  ['privacyPolicy.eventsInvitations', 'privacyPolicy.eventsInvitationsContent'],
  ['privacyPolicy.eventsLinkedIn', 'privacyPolicy.eventsLinkedInContent'],
  ['privacyPolicy.eventsRights', 'privacyPolicy.eventsRightsContent'],
] as const

export const PrivacyPolicy: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const navigation = useNavigation()
  const styles = getStyles(theme)
  const isLoggedIn = !!useCurrentUserStore((state) => state.userID)

  // Opened straight from a link (no page behind it): go to the start page.
  const goBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack()
      return
    }
    navigation.dispatch(
      CommonActions.reset({ index: 0, routes: [{ name: isLoggedIn ? 'Tab' : 'Auth' }] }),
    )
  }

  return (
    <Screen>
      <Header
        title={t('webFooter.privacyPolicy')}
        iconLeft="arrowBack"
        onPressLeft={goBack}
      />
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.section}>
          <Text type="body" color={theme.colors.secondary}>
            {t('privacyPolicy.introduction')}
          </Text>
        </View>

        <View style={styles.section}>
          <Text type="headline" color={theme.colors.black} style={styles.sectionTitle}>
            {t('privacyPolicy.dataCollection')}
          </Text>
          <Text type="body" color={theme.colors.secondary}>
            {t('privacyPolicy.dataCollectionContent')}
          </Text>
        </View>

        <View style={styles.section}>
          <Text type="headline" color={theme.colors.black} style={styles.sectionTitle}>
            {t('privacyPolicy.dataUsage')}
          </Text>
          <Text type="body" color={theme.colors.secondary}>
            {t('privacyPolicy.dataUsageContent')}
          </Text>
        </View>

        <View style={styles.section}>
          <Text type="headline" color={theme.colors.black} style={styles.sectionTitle}>
            {t('privacyPolicy.dataProtection')}
          </Text>
          <Text type="body" color={theme.colors.secondary}>
            {t('privacyPolicy.dataProtectionContent')}
          </Text>
        </View>

        <View style={styles.section}>
          <Text type="headline" color={theme.colors.black} style={styles.sectionTitle}>
            {t('privacyPolicy.cookies')}
          </Text>
          <Text type="body" color={theme.colors.secondary}>
            {t('privacyPolicy.cookiesContent')}
          </Text>
        </View>

        <View style={styles.section}>
          <Text type="headline" color={theme.colors.black} style={styles.sectionTitle}>
            {t('privacyPolicy.rights')}
          </Text>
          <Text type="body" color={theme.colors.secondary}>
            {t('privacyPolicy.rightsContent')}
          </Text>
        </View>

        <View style={styles.section} testID="privacy-events-section">
          <Text type="headline" color={theme.colors.black} style={styles.sectionTitle}>
            {t('privacyPolicy.eventsTitle')}
          </Text>
          <Text type="body" color={theme.colors.secondary}>
            {t('privacyPolicy.eventsIntro')}
          </Text>
        </View>
        {EVENTS_BLOCKS.map(([title, content]) => (
          <View key={title} style={styles.section}>
            <Text type="subtitleBold" color={theme.colors.black} style={styles.sectionTitle}>
              {t(title)}
            </Text>
            <Text type="body" color={theme.colors.secondary}>
              {t(content)}
            </Text>
          </View>
        ))}
      </ScrollView>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    content: {
      flex: 1,
      paddingHorizontal: theme.spacing.xl,
    },
    section: {
      marginBottom: theme.spacing.xxl,
    },
    sectionTitle: {
      marginBottom: theme.spacing.sm,
    },
  })
